const checkConditions = (field, conditions) => {
    if (field === null || field === undefined) return false;

    return conditions.some(
        (condition) =>
            field === condition ||
            (Array.isArray(field) && field?.some((option) => option.value === condition)) ||
            (field?.value && field.value === condition)
    );
};

// Helper functions for file validation
const isEmpty = (v) => v === null || (Array.isArray(v) && v.length === 0);

const toFiles = (value) => {
    if (isEmpty(value)) return [];
    if (value instanceof File) return [value];
    if (value instanceof FileList) return Array.from(value);
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'object') {
        // Handles objects like {0: File, 1: File} or custom wrappers
        return Object.values(value).filter(
            (f) => f && (f instanceof File || (typeof f.size === 'number' && 'type' in f))
        );
    }
    return [];
};

const hasAllowedType = (file, allowedMimes, allowedExts) => {
    if (allowedMimes.includes(file.type)) return true;
    const name = (file.name || '').toLowerCase();
    const dot = name.lastIndexOf('.');
    const ext = dot >= 0 ? name.slice(dot) : '';
    return allowedExts.includes(ext);
};

export const requiredIf = (conditions) => {
    return {
        is: (value) => checkConditions(value, conditions),
        then: (schema) => schema.required('"${label}" is a required field.'),
        otherwise: (schema) => schema.notRequired(),
    };
};

export const requiredCheck = (dependent, conditions) => {
    return (values) => {
        return checkConditions(values[dependent], conditions);
    };
};

export const displayIf = (dependent, conditions) => {
    return (values) => {
        return checkConditions(values[dependent], conditions);
    };
};

export const conditionallyRequired = (dependent, conditions) => {
    return {
        name: 'required-if',
        message: ({ label }) => `"${label}" is a required field`,
        test: function (value, context) {
            const { parent } = this;
            if (checkConditions(parent[dependent], conditions)) {
                return value !== null && value !== '';
            }
            return true;
        },
    };
};

export const requiredTrue = () => {
    return {
        name: 'required-true',
        message: ({ label }) => `"${label}" is a required field.`,
        test: function (value, context) {
            return value !== null && value !== '' && value !== false;
        },
    };
};

// File validation tests
export const fileSize = (maxBytes) => {
    return {
        name: 'file-size',
        message: `Each file must be smaller than ${Math.floor(maxBytes / (1024 * 1024))} MB`,
        test: function (value) {
            const files = toFiles(value);
            if (!files.length) return true; // let "required" handle empties
            return files.every((f) => typeof f.size === 'number' && f.size <= maxBytes);
        },
    };
};

export const fileType = (allowedMimes, allowedExts) => {
    return {
        name: 'file-type',
        message: `Unsupported file type. Allowed: ${allowedExts.join(', ')}`,
        test: function (value) {
            const files = toFiles(value);
            if (!files.length) return true;
            return files.every((f) => hasAllowedType(f, allowedMimes, allowedExts));
        },
    };
};

export const validRating = (defaultRating) => {
    return {
        name: 'must-be-valid-rating',
        message: 'Invalid rating, please select a valid option',
        test: function (value) {
            const rating = Array.isArray(value) ? value[0] : value;
            return rating?.value && rating.value !== defaultRating;
        },
    };
};

export const validFile = () => {
    return {
        name: 'must-be-valid-file',
        message: 'One or more files are missing or invalid',
        test: function (value) {
            const files = toFiles(value);
            if (!files.length) return false;
            return files.every(
                (f) => f instanceof File || (typeof f.size === 'number' && 'type' in f)
            );
        },
    };
};

export const requiredMessage = '"${label}" is a required field.';
